/**
 * conference-details block (1 use)
 *
 * Conference details page (IGAD landing) with hero image, event description,
 * location/booth info, and a featured event section with date card SVG.
 * Contains mostly hardcoded content for Oktane conference.
 *
 * Hugo template: blocks/conference-details.html
 */
interface EventSection {
  heading?: string;
  copy?: string;
  copy_2?: string;
  image?: string;
  date_tags?: string[];
}

export function ConferenceDetails({
  hero_image,
  hero_image_xl,
  title,
  description,
  description_2,
  location_badges,
  events,
}: {
  hero_image?: string;
  hero_image_xl?: string;
  title?: string;
  description?: string;
  description_2?: string;
  location_badges?: string[];
  events?: EventSection[];
}) {
  return (
    <div className="px-4 md:px-8 pb-16 lg:pb-28">
      <div className="max-w-screen-xl mx-auto">
        {/* Hero image */}
        {(hero_image || hero_image_xl) && (
          <div className="rounded-2xl overflow-hidden">
            {hero_image_xl && (
              <img
                src={hero_image_xl}
                alt=""
                className="hidden xl:block w-full"
                loading="lazy"
              />
            )}
            {hero_image && (
              <img
                src={hero_image}
                alt=""
                className={hero_image_xl ? "xl:hidden w-full" : "w-full"}
                loading="lazy"
              />
            )}
          </div>
        )}

        {title && (
          <h1 className="font-display text-charcoal font-bold text-25 mt-12 md:mt-16 lg:text-31 max-w-3xl">
            {title}
          </h1>
        )}
        {description && (
          <p className="font-body text-gray-80 text-25 mt-4 md:mt-5 max-w-5xl">
            {description}
          </p>
        )}
        {description_2 && (
          <p className="font-body text-gray-80 text-25 mt-4 max-w-5xl">
            {description_2}
          </p>
        )}

        {location_badges && location_badges.length > 0 && (
          <ul className="flex flex-wrap gap-4 mt-6 max-w-xl">
            {location_badges.map((badge, i) => (
              <li
                key={i}
                className="font-medium text-base text-teal-dark bg-teal-dark/10 rounded-full px-3.5 py-1.5 leading-tight flex items-center gap-1.5"
              >
                {badge}
              </li>
            ))}
          </ul>
        )}

        <hr className="border-gray-60 mt-12 md:mt-16 lg:mt-20 2xl:mt-28" />

        {events?.map((event, i) => (
          <div
            key={i}
            className="grid gap-6 lg:gap-10 lg:grid-cols-12 mt-9 lg:mt-10 2xl:mt-16 group"
          >
            {event.image && (
              <div className="lg:col-span-5">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={event.image}
                    alt=""
                    className="group-hover:scale-[1.05] transition-all duration-500 w-full"
                    loading="lazy"
                  />
                </div>
              </div>
            )}
            <div className="lg:col-span-7">
              {event.heading && (
                <h2 className="font-display text-charcoal font-bold text-25">
                  {event.heading}
                </h2>
              )}
              {event.copy && (
                <p className="font-body text-gray-80 mt-4 hidden md:block">
                  {event.copy}
                </p>
              )}
              {event.copy_2 && (
                <p className="font-body text-gray-80 mt-2 hidden md:block">
                  {event.copy_2}
                </p>
              )}
              {event.date_tags && (
                <ul className="flex flex-wrap gap-1.5 mt-5 max-w-xl">
                  {event.date_tags.map((tag, j) => (
                    <li
                      key={j}
                      className="font-medium text-13 text-teal-dark bg-teal-dark/10 rounded-full px-2.5 py-0.5 leading-tight"
                    >
                      {tag}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
